import { getCache } from '../apollo/Cache';

import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import {
  CoreTokenFieldsFragment,
  LogoutMutation,
  RefreshAccessTokenDocument,
  useLogoutMutation
} from '../models/graphql';
import { FetchResult } from '@apollo/client/link/core/types';
import { setSessionToast } from '../helpers/sessionToastHelpers';
import { useContext } from 'react';
import { AppContext, changeSelectedWorkspaceId } from '../providers/AppProvider';
import { terminatingLink } from '../apollo/terminatingLink';

gql`
  mutation Logout {
    logout
  }

  mutation RefreshAccessToken {
    refreshAccessToken {
      ...CoreTokenFields
    }
  }
`;

export type Auth = {
  isLoggedIn: boolean;
  logout: (pathAfterLogout?: string) => Promise<FetchResult<LogoutMutation>>;
};

// A client that only uses the httpLink for the refresh mutation.
let refreshTokenApiClient: ApolloClient<NormalizedCacheObject>;
const getRefreshTokenApiClient = () => {
  refreshTokenApiClient =
    refreshTokenApiClient ||
    new ApolloClient({
      link: terminatingLink,
      cache: getCache()
    });
  return refreshTokenApiClient;
};

export const getNewAccessToken = async () => {
  const {
    data: { refreshAccessToken }
  } = await getRefreshTokenApiClient().mutate({
    mutation: RefreshAccessTokenDocument
  });

  storeAuthToken(refreshAccessToken);
};

export const refreshTokenBeforeExpiration = () => {
  const expiresAt = parseInt(localStorage.getItem('tokenExpiresAt') || '');
  if (!Number.isInteger(expiresAt)) {
    return;
  }

  const waitInSeconds = expiresAt - Math.floor(Date.now() / 1000) - 60 * 5;
  if (waitInSeconds < 1) {
    // Too late to refresh before expiration. Let the ErrorLink take care of it on next request.
    //console.log('Auth: Token expired, not refreshing');
    return;
  }

  //console.log('Auth: Waiting ' + waitInSeconds + ' seconds to refresh access token');
  setTimeout(() => {
    //console.log('Auth: Refreshing access token');
    getNewAccessToken();
  }, waitInSeconds * 1000);
};

export const storeAuthToken = (token: CoreTokenFieldsFragment) => {
  localStorage.setItem('token', token.token);
  localStorage.setItem('tokenExpiresAt', '' + (Math.floor(Date.now() / 1000) + token.expiresIn));
  refreshTokenBeforeExpiration();
};

// On initial load, start the token refresh timer logic.
refreshTokenBeforeExpiration();

export const useAuth = (): Auth => {
  const { isLoggedIn, showLoadingOverlay } = useContext(AppContext);
  const [logoutMutation] = useLogoutMutation();

  const logout: Auth['logout'] = pathAfterLogout => {
    showLoadingOverlay(true);
    const promise = logoutMutation();

    promise
      .then(() => {
        localStorage.clear();
        // TODO When we only allow logging in through React app, we can use the changeSelectedWorkspaceId() instead of full page load.
        // TODO This is because we render the legacy login portal for now.
        window.location.href = pathAfterLogout || '/login';
        if (false) {
          setSessionToast({ severity: 'success', summary: 'Log Out', detail: 'You have logged out successfully.' });
          changeSelectedWorkspaceId('', pathAfterLogout || '/login');
        }
      })
      .catch(() => {
        showLoadingOverlay(false);
      });

    return promise;
  };

  return {
    logout,
    isLoggedIn
  };
};
